import React, { useState, useEffect } from 'react';
import { Button, DialogActions, DialogContent, Divider, Typography, IconButton, Checkbox, FormControlLabel } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { getCurrentUser, loadReport } from '../api/cashReports';
import { manageShift } from '../api/shift'; // Corrected import path (was '../api/shift')
import { useNavigate } from 'react-router-dom';
import { processDay } from '../internals/scripts/closing';

// Custom styled dialog component
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const ShiftButton = ({ selectedDate }) => {
  const [shiftActive, setShiftActive] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [open, setOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [reportExists, setReportExists] = useState(false);
  const [updateReports, setUpdateReports] = useState(false);
  const navigate = useNavigate();

  // Open the confirmation dialog
  const handleClickOpen = (action) => {
    setConfirmAction(action);
    setOpen(true);
  };

  // Close the dialog without action
  const handleClose = () => {
    setOpen(false);
  };

  // Check shift status and update state
  const checkShiftStatus = async () => {
    try {
      const result = await manageShift({ operation: 'check' }); // No isGlobal since it’s a single row
      if (result.shift.is_active) {
        setShiftActive(true);
        const startTime = new Date(result.shift.created_at).getTime();
        const currentTime = Date.now();
        const initialElapsed = Math.floor((currentTime - startTime) / 1000);
        setElapsedTime(initialElapsed);
      } else {
        setShiftActive(false);
        setElapsedTime(0);
      }
    } catch (error) {
      console.error('Error checking shift status:', error.message);
      setShiftActive(false);
      setElapsedTime(0);
    }
  };

  // Confirm the action and start/end the shift
  const handleConfirm = async () => {
    try {
      if (confirmAction === 'start') {
        const result = await manageShift({ operation: 'start' });
        setOpen(false);
        if (result.shift) {
          setShiftActive(true);
          setElapsedTime(0);
        }
      } else if (confirmAction === 'end') {
        const result = await manageShift({ operation: 'stop' });
        setOpen(false);
        if (result.shift || result.message === 'Shift stopped') {
          setShiftActive(false);
          setElapsedTime(0);
          if (updateReports) {
            await processDay(selectedDate);
          }
        }
      }
      // Re-check shift status after action to ensure UI syncs with DB
      await checkShiftStatus();

      // Navigate or reload
      if (window.location.pathname === '/') {
        window.location.reload();
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Shift action error:', error.message);
    }
    setOpen(false);
  };

  // Format time in HH:MM:SS
  const formatTime = (elapsedTime) => {
    const hours = Math.floor(elapsedTime / 3600);
    const minutes = Math.floor((elapsedTime % 3600) / 60);
    const seconds = elapsedTime % 60;

    const formattedHours = hours < 10 ? `0${hours}` : hours;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  };

  // Check if report exists for the selected date
  useEffect(() => {
    const checkReport = async () => {
      try {
        const user = await getCurrentUser();
        if (!user) return;

        const reportData = await loadReport(selectedDate, user.id);
        setReportExists(!!reportData);
      } catch (error) {
        console.error('Error loading report:', error);
        setReportExists(false);
      }
    };

    checkReport();
  }, [selectedDate]);

  // Check shift status and set up the timer
  useEffect(() => {
    let interval;

    checkShiftStatus().then(() => {
      if (shiftActive) {
        interval = setInterval(() => {
          setElapsedTime((prevTime) => prevTime + 1);
        }, 1000);
      }
    });

    return () => clearInterval(interval);
  }, [shiftActive]); // Add shiftActive as dependency to re-run when it changes

  return (
    <>
      <Button
        variant="outlined"
        color={shiftActive ? 'error' : 'success'}
        size="small"
        startIcon={shiftActive ? <StoreRoundedIcon /> : <PlayArrowRoundedIcon />}
        onClick={() => handleClickOpen(shiftActive ? 'end' : 'start')}
      >
        {shiftActive ? formatTime(elapsedTime) : 'Schicht starten'}
      </Button>

      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        sx={{
          '& .MuiDialog-paper': {
            width: '400px',
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          {shiftActive ? 'Schicht beenden' : 'Schicht starten'}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            {shiftActive ? (
              'Bist du sicher, dass du deine Schicht beenden möchtest?'
            ) : reportExists ? (
              'Bist du sicher, dass du deine Schicht starten möchtest?'
            ) : (
              <>
                <Typography
                  component="h2"
                  variant="subtitle2"
                  gutterBottom
                  color="warning"
                  sx={{ fontWeight: '600' }}
                >
                  Kassenbericht
                </Typography>
                <Typography sx={{ color: 'text.secondary', mb: 2 }}>
                  Dein täglicher Kassenbericht fehlt noch.{' '}
                  <span
                    style={{ color: 'blue', cursor: 'pointer' }}
                    onClick={() => { navigate('/register'); setOpen(false); }}
                  >
                    Klicke hier
                  </span>
                  , um ihn zu erstellen!
                </Typography>
              </>
            )}
          </Typography>
          {shiftActive && (
            <>
              <Typography
                component="h2"
                variant="subtitle2"
                color="warning"
                gutterBottom
                sx={{ fontWeight: '600', mt: 5 }}
              >
                Aktualisieren
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: '8px' }}>
                Klicke das Kästchen an, um den Kassenbericht und das Zahlprotokoll zu aktualisieren
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={updateReports}
                    onChange={(e) => setUpdateReports(e.target.checked)}
                    name="updateReports"
                    color="primary"
                  />
                }
                label="Möchtest du das Zahlprotokoll und den Kassenbericht aktualisieren?"
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
          <Button onClick={handleClose} color="primary" variant="outlined">
            Abbrechen
          </Button>
          <Button onClick={handleConfirm} color="secondary" variant="outlined">
            {shiftActive ? 'Schicht beenden' : 'Schicht starten'}
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </>
  );
};

export default ShiftButton;